import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  ModerationAction, ModerationActionValue, MODERATION_ACTION_LABELS, REPORT_REASON_LABELS,
  Report, reportService, UserModerationSummary,
} from '../services/reportService';

const COPY: Record<ModerationActionValue, { title: string; blurb: string; noteLabel: string; noteRequired: boolean; confirm: string }> = {
  RemoveContent: {
    title: 'Remove content',
    blurb: 'Deletes the reported post, comment or event for everyone and resolves every open report on it. The author is notified.',
    noteLabel: 'Reason shown to the author (optional)',
    noteRequired: false,
    confirm: 'Remove',
  },
  WarnUser: {
    title: 'Warn user',
    blurb: 'Sends the author an in-app warning and resolves the open reports on this content. Nothing is removed.',
    noteLabel: 'Warning shown to the user',
    noteRequired: true,
    confirm: 'Send warning',
  },
  SuspendUser: {
    title: 'Suspend user',
    blurb: 'Locks the author out of the app (existing sessions included) until the suspension ends. They can still be reinstated early from their history.',
    noteLabel: 'Reason shown to the user',
    noteRequired: true,
    confirm: 'Suspend',
  },
};

export const ModerationActionDialog: React.FC<{
  report: Report;
  action: ModerationActionValue;
  onClose: () => void;
  onDone: (updated: Report) => void;
}> = ({ report, action, onClose, onDone }) => {
  const copy = COPY[action];
  const [note, setNote] = useState('');
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !submitting && (!copy.noteRequired || note.trim().length > 0);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      onDone(await reportService.takeAction(report.reportId, action, note.trim() || undefined, action === ModerationAction.SuspendUser ? days : undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply action');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title}: {report.targetAuthorDisplayName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{copy.blurb}</Typography>
          {report.targetTextSnapshot && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Reported {report.targetType.toLowerCase()}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{report.targetTextSnapshot}</Typography>
            </Box>
          )}
          {action === ModerationAction.SuspendUser && (
            <TextField select size="small" label="Length" value={days} onChange={e => setDays(Number(e.target.value))}>
              {[1, 3, 7, 14, 30, 90, 365].map(d => <MenuItem key={d} value={d}>{d} day{d === 1 ? '' : 's'}</MenuItem>)}
            </TextField>
          )}
          <TextField
            label={copy.noteLabel}
            value={note}
            onChange={e => setNote(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color={action === ModerationAction.WarnUser ? 'warning' : 'error'}
          onClick={submit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {copy.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** A user's suspension status and every report ever filed against them — what an admin needs before acting. */
export const UserHistoryDialog: React.FC<{ userId: string; onClose: () => void; onChanged?: () => void }> = ({ userId, onClose, onChanged }) => {
  const [summary, setSummary] = useState<UserModerationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lifting, setLifting] = useState(false);

  const load = useCallback(async () => {
    try {
      setSummary(await reportService.getUserModeration(userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user history');
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const lift = async () => {
    setLifting(true);
    try {
      await reportService.liftSuspension(userId);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lift suspension');
    } finally {
      setLifting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{summary ? `History: ${summary.displayName}` : 'User history'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!summary && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
        {summary && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {summary.isSuspended ? (
              <Alert
                severity="warning"
                action={<Button color="inherit" size="small" onClick={lift} disabled={lifting}>Lift suspension</Button>}
              >
                Suspended until {new Date(summary.suspendedUntil!).toLocaleDateString()}
                {summary.suspensionReason ? ` — ${summary.suspensionReason}` : ''}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">Not currently suspended.</Typography>
            )}
            <Typography variant="subtitle2">
              {summary.reportsAgainst.length} report{summary.reportsAgainst.length === 1 ? '' : 's'} against this user
            </Typography>
            {summary.reportsAgainst.map((r, i) => (
              <Box key={r.reportId}>
                {i > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">{new Date(r.createdAt).toLocaleString()}</Typography>
                  <Chip size="small" label={REPORT_REASON_LABELS[r.reason]} />
                  <Chip size="small" variant="outlined" label={r.status} />
                  {r.actionTaken && <Chip size="small" color="error" variant="outlined" label={MODERATION_ACTION_LABELS[r.actionTaken]} />}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {r.targetType === 'Comment' ? 'Comment: ' : r.targetType === 'Event' ? 'Event: ' : ''}{r.targetTextSnapshot || '(no text)'}
                </Typography>
                {r.actionNote && <Typography variant="caption" color="text.secondary">Note: {r.actionNote}</Typography>}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};
